import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { trackBookingFunnel } from "@/lib/analytics";
import { shop } from "@/lib/copy/shop";
import { formatUsPhone } from "@/lib/phone";

type BikeBrandOption = { slug: string; label: string };
type Slot = { start: string; end: string };
type Hold = { start: string; end: string; expiresAt: string };
type Minor = { name: string };
type Adult = { name: string; minors: Minor[] };
type Rider = { position: number; adult_name: string; minors: string[]; signed: boolean };
type Waiver = { label: string; content_sha256: string; legal_text: string; preview_only: boolean };
type RiderLimits = { max_adults: number; max_minors_per_adult: number };
type Step = "slots" | "details" | "waiver" | "confirmed";

type ApiErrorPayload = {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string[]>;
    confirmed?: boolean;
  };
};

class BookingApiError extends Error {
  code: string;
  status: number;
  fields: Record<string, string[]>;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.error?.message || "We could not complete that step.");
    this.code = payload.error?.code || "booking_error";
    this.status = status;
    this.fields = payload.error?.fields || {};
  }
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
}

async function bookingRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method || "GET").toUpperCase();
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (method !== "GET" && method !== "HEAD") {
    headers.set("Content-Type", "application/json");
    headers.set("X-CSRF-Token", csrfToken());
  }

  const response = await fetch(path, { ...options, headers, credentials: "same-origin" });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new BookingApiError(response.status, payload);
  return payload as T;
}

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Denver",
  weekday: "short",
  month: "short",
  day: "numeric",
});

const timeFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Denver",
  hour: "numeric",
  minute: "2-digit",
});

const localDateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Denver",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function localDateKey(value: string | Date) {
  const date = typeof value === "string" ? new Date(value) : value;
  const parts = localDateFormatter.formatToParts(date);
  const valueFor = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || "";
  return `${valueFor("year")}-${valueFor("month")}-${valueFor("day")}`;
}

function isoDateRange(start: string, end: string) {
  const values: string[] = [];
  const current = new Date(`${start}T12:00:00Z`);
  const last = new Date(`${end}T12:00:00Z`);
  while (current <= last) {
    values.push(current.toISOString().slice(0, 10));
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return values;
}

function dateForLabel(date: string) {
  return new Date(`${date}T18:00:00Z`);
}

function safeErrorCode(error: unknown) {
  return error instanceof BookingApiError ? error.code : "network_error";
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "We could not complete that step.";
}

function HoldTimer({ hold }: { hold: Hold }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.ceil((new Date(hold.expiresAt).getTime() - Date.now()) / 1000)));

  useEffect(() => {
    const update = () => setRemaining(Math.max(0, Math.ceil((new Date(hold.expiresAt).getTime() - Date.now()) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [hold.expiresAt]);

  const minutes = Math.floor(remaining / 60);
  const seconds = String(remaining % 60).padStart(2, "0");
  const announcedMinutes = Math.max(1, Math.ceil(remaining / 60));
  return (
    <div className="headless-booking__hold" data-testid="booking-hold" role="group" aria-label="Temporary appointment hold">
      <span aria-hidden="true"><strong>{minutes}:{seconds}</strong> left to finish booking</span>
      <span className="headless-booking__sr-only" role="status" aria-live="polite">
        About {announcedMinutes} {announcedMinutes === 1 ? "minute" : "minutes"} left to finish booking.
      </span>
      <span className="headless-booking__hold-slot">
        Chosen time: {dateFormatter.format(new Date(hold.start))} at {timeFormatter.format(new Date(hold.start))}
      </span>
    </div>
  );
}

function FieldError({ id, messages }: { id?: string; messages?: string[] }) {
  if (!messages?.length) return null;
  return (
    <div id={id} className="headless-booking__field-error" role="alert">
      {messages.join(" ")}
    </div>
  );
}

export function HeadlessBookingFlow({
  bikeBrands,
  preselectedBikeBrand,
  startDate,
  endDate,
  riderLimits,
}: {
  bikeBrands: BikeBrandOption[];
  preselectedBikeBrand?: string | null;
  startDate: string;
  endDate: string;
  riderLimits: RiderLimits;
}) {
  const dates = useMemo(() => isoDateRange(startDate, endDate), [startDate, endDate]);
  const dateButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const viewedTracked = useRef(false);
  const waiverStartedTracked = useRef(false);
  const waiverCompletedTracked = useRef(false);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const previousStepRef = useRef<Step>("slots");
  const [step, setStep] = useState<Step>("slots");
  const [resumeStep, setResumeStep] = useState<Step>("details");
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsStatus, setSlotsStatus] = useState<"loading" | "ready" | "unavailable">("loading");
  const [selectedDate, setSelectedDate] = useState(startDate);
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [hold, setHold] = useState<Hold | null>(null);
  const [confirmedSlot, setConfirmedSlot] = useState<Slot | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<BookingApiError | null>(null);
  const [intakeSaved, setIntakeSaved] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [messageConsent, setMessageConsent] = useState(false);
  const [selectedBikeBrands, setSelectedBikeBrands] = useState<string[]>(preselectedBikeBrand ? [preselectedBikeBrand] : []);
  const [preparationNotes, setPreparationNotes] = useState("");
  const [adults, setAdults] = useState<Adult[]>([{ name: "", minors: [] }]);
  const [waiver, setWaiver] = useState<Waiver | null>(null);
  const [riders, setRiders] = useState<Rider[]>([]);
  const [typedNames, setTypedNames] = useState<Record<number, string>>({});
  const [assents, setAssents] = useState<Record<number, boolean>>({});

  const fetchSlots = useCallback(async () => {
    setSlotsStatus("loading");
    setError(null);
    try {
      const response = await bookingRequest<{ slots: Slot[] }>(
        `/book/slots?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`,
      );
      setSlots(response.slots);
      setSlotsStatus("ready");
      const firstAvailable = dates.find((date) => response.slots.some((slot) => localDateKey(slot.start) === date));
      setSelectedDate((current) => {
        if (response.slots.some((slot) => localDateKey(slot.start) === current)) return current;
        return firstAvailable || current;
      });
    } catch (caught) {
      setSlotsStatus("unavailable");
      setError(caught instanceof BookingApiError ? caught : new BookingApiError(0, {
        error: { code: "network_error", message: "Scheduling is temporarily unavailable. No appointment has been made." },
      }));
      trackBookingFunnel("failed", safeErrorCode(caught));
    }
  }, [dates, endDate, startDate]);

  useEffect(() => {
    if (!viewedTracked.current) {
      viewedTracked.current = true;
      trackBookingFunnel("viewed");
    }
    void fetchSlots();
  }, [fetchSlots]);

  useEffect(() => {
    if (previousStepRef.current === step) return;
    previousStepRef.current = step;
    const frame = window.requestAnimationFrame(() => stepHeadingRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [step]);

  useEffect(() => {
    if (!hold) return;
    const expires = new Date(hold.expiresAt).getTime();
    const timer = window.setInterval(() => {
      if (Date.now() < expires) return;
      window.clearInterval(timer);
      setResumeStep(step === "slots" || step === "confirmed" ? "details" : step);
      setHold(null);
      setSelectedStart(null);
      setStep("slots");
      setNotice("Your 15-minute hold expired. Your answers are saved; choose another time to continue.");
    }, 500);
    return () => window.clearInterval(timer);
  }, [hold, step]);

  const slotsForDate = slots.filter((slot) => localDateKey(slot.start) === selectedDate);
  const allSigned = riders.length > 0 && riders.every((rider) => rider.signed);
  const nextRider = riders.find((rider) => !rider.signed);

  async function selectSlot(slot: Slot) {
    setBusy(true);
    setError(null);
    setNotice(null);
    setSelectedStart(slot.start);
    try {
      const response = await bookingRequest<{
        status: string;
        start: string;
        end: string;
        expires_at: string;
      }>("/book/holds", {
        method: "POST",
        body: JSON.stringify({ start: slot.start }),
      });
      setHold({ start: response.start, end: response.end, expiresAt: response.expires_at });
      trackBookingFunnel("slot_selected");
      setStep(intakeSaved ? (waiver ? "waiver" : resumeStep) : "details");
    } catch (caught) {
      const apiError = caught instanceof BookingApiError ? caught : new BookingApiError(0, {
        error: { code: "network_error", message: "We could not hold that time. Choose it again or try another." },
      });
      setError(apiError);
      setSelectedStart(null);
      trackBookingFunnel("failed", apiError.code);
      if (apiError.status === 409) {
        setNotice(apiError.message);
        void fetchSlots();
      }
    } finally {
      setBusy(false);
    }
  }

  function handleDateKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    let next = index;
    if (event.key === "ArrowLeft") next = Math.max(0, index - 1);
    if (event.key === "ArrowRight") next = Math.min(dates.length - 1, index + 1);
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = dates.length - 1;
    setSelectedDate(dates[next]);
    dateButtonRefs.current[next]?.focus();
  }

  function updateAdult(index: number, updates: Partial<Adult>) {
    setAdults((current) => current.map((adult, position) => position === index ? { ...adult, ...updates } : adult));
  }

  function addMinor(adultIndex: number) {
    if (adults[adultIndex].minors.length >= riderLimits.max_minors_per_adult) return;
    updateAdult(adultIndex, { minors: [...adults[adultIndex].minors, { name: "" }] });
  }

  function addAdult() {
    setAdults((current) => current.length >= riderLimits.max_adults ? current : [...current, { name: "", minors: [] }]);
  }

  function toggleBikeBrand(slug: string) {
    setSelectedBikeBrands((current) => current.includes(slug)
      ? current.filter((brand) => brand !== slug)
      : [...current, slug]);
  }

  function updatePrimaryName(nextFirstName: string, nextLastName: string) {
    const previousPrimaryName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const nextPrimaryName = `${nextFirstName.trim()} ${nextLastName.trim()}`.trim();
    const firstAdultSigned = riders.find((rider) => rider.position === 0)?.signed;
    if (!firstAdultSigned) {
      setAdults((current) => current.map((adult, position) => {
        if (position !== 0 || (adult.name.trim() && adult.name !== previousPrimaryName)) return adult;
        return { ...adult, name: nextPrimaryName };
      }));
    }
    setFirstName(nextFirstName);
    setLastName(nextLastName);
  }

  function updateMinor(adultIndex: number, minorIndex: number, name: string) {
    const minors = adults[adultIndex].minors.map((minor, position) => position === minorIndex ? { name } : minor);
    updateAdult(adultIndex, { minors });
  }

  function removeMinor(adultIndex: number, minorIndex: number) {
    updateAdult(adultIndex, { minors: adults[adultIndex].minors.filter((_, position) => position !== minorIndex) });
  }

  async function loadWaiver() {
    const response = await bookingRequest<{ waiver: Waiver; riders: Rider[] }>("/book/waiver");
    setWaiver(response.waiver);
    setRiders(response.riders);
    if (!waiverStartedTracked.current) {
      waiverStartedTracked.current = true;
      trackBookingFunnel("waiver_started");
    }
    return response;
  }

  async function saveDetails(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const primaryName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const submittedAdults = adults.map((adult, index) => ({
      ...adult,
      name: index === 0 && !adult.name.trim() ? primaryName : adult.name,
    }));
    setAdults(submittedAdults);

    try {
      await bookingRequest("/book/intake", {
        method: "PATCH",
        body: JSON.stringify({
          booking: {
            first_name: firstName,
            last_name: lastName,
            email,
            mobile_phone: mobilePhone,
            transactional_message_consent: messageConsent,
            bike_brands: selectedBikeBrands,
            preparation_notes: preparationNotes || null,
            adults: submittedAdults,
          },
        }),
      });
      setIntakeSaved(true);
      await loadWaiver();
      setStep("waiver");
    } catch (caught) {
      const apiError = caught instanceof BookingApiError ? caught : new BookingApiError(0, {
        error: { code: "network_error", message: "We could not save these details. Try again." },
      });
      setError(apiError);
      trackBookingFunnel("failed", apiError.code);
    } finally {
      setBusy(false);
    }
  }

  async function signCurrentRider(event: FormEvent) {
    event.preventDefault();
    if (!nextRider) return;
    setBusy(true);
    setError(null);
    try {
      const result = await bookingRequest<{ all_adults_signed: boolean }>("/book/signatures", {
        method: "POST",
        body: JSON.stringify({
          position: nextRider.position,
          typed_name: typedNames[nextRider.position] || "",
          accepted: assents[nextRider.position] || false,
        }),
      });
      await loadWaiver();
      if (result.all_adults_signed && !waiverCompletedTracked.current) {
        waiverCompletedTracked.current = true;
        trackBookingFunnel("waiver_completed");
      }
    } catch (caught) {
      const apiError = caught instanceof BookingApiError ? caught : new BookingApiError(0, {
        error: { code: "network_error", message: "We could not save this signature. Try again." },
      });
      setError(apiError);
      trackBookingFunnel("failed", apiError.code);
    } finally {
      setBusy(false);
    }
  }

  function addAdultAfterSignature() {
    if (adults.length >= riderLimits.max_adults) return;
    addAdult();
    setStep("details");
    setNotice("Add the adult rider, then save the rider details again. That adult will sign next.");
  }

  async function confirmBooking() {
    setBusy(true);
    setError(null);
    try {
      const result = await bookingRequest<{
        status: string;
        confirmed: boolean;
        start: string;
        end: string;
      }>("/book/confirm", { method: "POST", body: "{}" });
      if (!result.confirmed || result.status !== "confirmed") {
        throw new BookingApiError(503, {
          error: { code: "confirmation_pending", message: "The appointment has not been confirmed yet." },
        });
      }
      setHold(null);
      setConfirmedSlot({ start: result.start, end: result.end });
      setStep("confirmed");
      trackBookingFunnel("confirmed");
    } catch (caught) {
      const apiError = caught instanceof BookingApiError ? caught : new BookingApiError(0, {
        error: { code: "network_error", message: "We could not verify the appointment. It is not shown as confirmed." },
      });
      setError(apiError);
      trackBookingFunnel("failed", apiError.code);
      if (apiError.code === "hold_expired" || apiError.code === "slot_unavailable") {
        setResumeStep("waiver");
        setHold(null);
        setSelectedStart(null);
        setStep("slots");
        void fetchSlots();
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="headless-booking" data-testid="headless-booking-flow">
      <p className="headless-booking__mobile-progress" aria-label="Booking progress">
        Step {{ slots: 1, details: 2, waiver: 3, confirmed: 4 }[step]} of 4 · {({ slots: "Choose a time", details: "Ride details", waiver: "Waiver", confirmed: "Confirmed" })[step]}
      </p>
      <ol className="headless-booking__steps" aria-label="Booking progress" role="list">
        {["Choose a time", "Ride details", "Waiver", "Confirmed"].map((label, index) => {
          const stepIndex = { slots: 0, details: 1, waiver: 2, confirmed: 3 }[step];
          const complete = index < stepIndex;
          return (
            <li key={label} aria-current={index === stepIndex ? "step" : undefined} data-complete={complete || undefined}>
              <span className="headless-booking__step-number" aria-label={complete ? `Step ${index + 1}, complete` : `Step ${index + 1}`}>
                {complete ? "✓" : index + 1}
              </span>
              <span className="headless-booking__step-label">{label}</span>
            </li>
          );
        })}
      </ol>

      {hold && step !== "confirmed" && <HoldTimer hold={hold} />}
      {notice && <div className="headless-booking__notice" role="status">{notice}</div>}
      {error && (
        <div className="headless-booking__error" role="alert">
          <strong>{error.code === "confirmation_pending" ? "Not confirmed yet." : error.code === "scheduling_unavailable" ? "We couldn’t reserve that time." : "Please check this step."}</strong>
          <span>{error.message}</span>
          {error.code === "scheduling_unavailable" && (
            <span>Try again, or <a href={shop.phoneHref}>call the shop</a> for help.</span>
          )}
        </div>
      )}

      {step === "slots" && (
        <section aria-labelledby="booking-date-heading">
          <h3 id="booking-date-heading" ref={stepHeadingRef} tabIndex={-1}>Choose a date</h3>
          <div className="headless-booking__dates" role="group" aria-label="Test-ride dates">
            {dates.map((date, index) => {
              const available = slots.some((slot) => localDateKey(slot.start) === date);
              return (
                <button
                  key={date}
                  ref={(node) => { dateButtonRefs.current[index] = node; }}
                  type="button"
                  aria-pressed={selectedDate === date}
                  aria-label={`${dateFormatter.format(dateForLabel(date))}${available ? "" : ", no times available"}`}
                  onClick={() => setSelectedDate(date)}
                  onKeyDown={(event) => handleDateKey(event, index)}
                >
                  <span>{dateFormatter.format(dateForLabel(date))}</span>
                  <small>{available ? "Times available" : "No times"}</small>
                </button>
              );
            })}
          </div>
          <p className="headless-booking__date-scroll-cue">Swipe to see more dates <span aria-hidden="true">→</span></p>

          <h3>Choose a time</h3>
          <div aria-live="polite">
            {slotsStatus === "loading" && <p>Checking available times…</p>}
            {slotsStatus === "unavailable" && (
              <button type="button" className="headless-booking__secondary" onClick={() => void fetchSlots()}>
                Try loading times again
              </button>
            )}
            {slotsStatus === "ready" && slotsForDate.length === 0 && (
              <p>No test-ride times are available on this date. Choose another date.</p>
            )}
            {slotsStatus === "ready" && slotsForDate.length > 0 && (
              <div className="headless-booking__times" role="group" aria-label={`Times for ${dateFormatter.format(dateForLabel(selectedDate))}`}>
                {slotsForDate.map((slot) => (
                  <button
                    type="button"
                    key={slot.start}
                    disabled={busy}
                    aria-pressed={selectedStart === slot.start}
                    onClick={() => void selectSlot(slot)}
                  >
                    {timeFormatter.format(new Date(slot.start))}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {step === "details" && (
        <form onSubmit={saveDetails} aria-labelledby="ride-details-heading">
          <h3 id="ride-details-heading" ref={stepHeadingRef} tabIndex={-1}>Tell us who is coming</h3>
          <p className="headless-booking__required-note">Fields marked * are required.</p>
          <div className="headless-booking__grid">
            <label>
              <span>First name<span className="headless-booking__required" aria-hidden="true">*</span></span>
              <input
                name="first_name"
                autoComplete="given-name"
                required
                aria-required="true"
                aria-invalid={Boolean(error?.fields.first_name)}
                aria-describedby={error?.fields.first_name?.length ? "booking-first-name-error" : undefined}
                maxLength={120}
                value={firstName}
                onChange={(event) => updatePrimaryName(event.target.value, lastName)}
              />
              <FieldError id="booking-first-name-error" messages={error?.fields.first_name} />
            </label>
            <label>
              <span>Last name<span className="headless-booking__required" aria-hidden="true">*</span></span>
              <input
                name="last_name"
                autoComplete="family-name"
                required
                aria-required="true"
                aria-invalid={Boolean(error?.fields.last_name)}
                aria-describedby={error?.fields.last_name?.length ? "booking-last-name-error" : undefined}
                maxLength={120}
                value={lastName}
                onChange={(event) => updatePrimaryName(firstName, event.target.value)}
              />
              <FieldError id="booking-last-name-error" messages={error?.fields.last_name} />
            </label>
            <label>
              <span>Email<span className="headless-booking__required" aria-hidden="true">*</span></span>
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                aria-required="true"
                aria-invalid={Boolean(error?.fields.email)}
                aria-describedby={error?.fields.email?.length ? "booking-email-error" : undefined}
                maxLength={254}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <FieldError id="booking-email-error" messages={error?.fields.email} />
            </label>
            <label>
              <span>Mobile phone<span className="headless-booking__required" aria-hidden="true">*</span></span>
              <input
                name="mobile_phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(303) 555-1234"
                required
                aria-required="true"
                aria-invalid={Boolean(error?.fields.mobile_phone)}
                aria-describedby={error?.fields.mobile_phone?.length ? "booking-mobile-phone-error" : undefined}
                maxLength={40}
                value={mobilePhone}
                onChange={(event) => setMobilePhone(formatUsPhone(event.target.value))}
              />
              <FieldError id="booking-mobile-phone-error" messages={error?.fields.mobile_phone} />
            </label>
          </div>

          <fieldset className="headless-booking__brand-fieldset">
            <legend>Brands you want to try (optional)</legend>
            <div className="headless-booking__brand-intro">
              <span>Choose as many as you want, or leave this blank if you are not sure.</span>
              <a href="/bikes" target="_blank" rel="noreferrer">Browse the bike catalog <span aria-hidden="true">↗</span></a>
            </div>
            <div className="headless-booking__brand-options">
              {bikeBrands.map((brand) => {
                const selected = selectedBikeBrands.includes(brand.slug);
                return (
                  <label className="headless-booking__brand-option" data-selected={selected || undefined} key={brand.slug}>
                    <input
                      type="checkbox"
                      name="bike_brands[]"
                      value={brand.slug}
                      checked={selected}
                      onChange={() => toggleBikeBrand(brand.slug)}
                    />
                    <span>{brand.label}</span>
                  </label>
                );
              })}
            </div>
            <FieldError messages={error?.fields.bike_brands} />
          </fieldset>
          <label>How would you use the bike? (optional)
            <textarea name="preparation_notes" maxLength={2000} rows={4} value={preparationNotes} onChange={(event) => setPreparationNotes(event.target.value)} placeholder="For example: carry two kids to school, haul groceries up hills, fit in a small garage, or replace a second car." />
            <FieldError messages={error?.fields.preparation_notes} />
          </label>

          <fieldset>
            <legend>Adult riders</legend>
            <p>Every adult who will ride must sign the waiver separately. Add children under the adult responsible for them.</p>
            <p className="headless-booking__rider-limits" id="booking-rider-limits">
              Up to {riderLimits.max_adults} adults, with up to {riderLimits.max_minors_per_adult} minors per adult.
            </p>
            {adults.map((adult, adultIndex) => {
              const signed = riders.find((rider) => rider.position === adultIndex)?.signed;
              return (
                <div className="headless-booking__rider" key={adultIndex}>
                  <div className="headless-booking__rider-heading">
                    <strong>Adult rider {adultIndex + 1}{signed ? " · signed" : ""}</strong>
                    {!signed && adults.length > 1 && (
                      <button type="button" className="headless-booking__text-button" onClick={() => setAdults((current) => current.filter((_, index) => index !== adultIndex))}>Remove adult</button>
                    )}
                  </div>
                  <label>Full legal name<input required disabled={signed} maxLength={120} value={adult.name} onChange={(event) => updateAdult(adultIndex, { name: event.target.value })} /></label>
                  {adult.minors.map((minor, minorIndex) => (
                    <div className="headless-booking__minor" key={minorIndex}>
                      <label>Child or minor name<input required disabled={signed} maxLength={120} value={minor.name} onChange={(event) => updateMinor(adultIndex, minorIndex, event.target.value)} /></label>
                      {!signed && <button type="button" className="headless-booking__text-button" onClick={() => removeMinor(adultIndex, minorIndex)}>Remove</button>}
                    </div>
                  ))}
                  {!signed && <button type="button" className="headless-booking__secondary" aria-describedby="booking-rider-limits" data-limit-reached={adult.minors.length >= riderLimits.max_minors_per_adult || undefined} disabled={adult.minors.length >= riderLimits.max_minors_per_adult} onClick={() => addMinor(adultIndex)}>Add a child or minor</button>}
                </div>
              );
            })}
            <button type="button" className="headless-booking__secondary" aria-describedby="booking-rider-limits" data-limit-reached={adults.length >= riderLimits.max_adults || undefined} disabled={adults.length >= riderLimits.max_adults} onClick={addAdult}>Add another adult</button>
            <FieldError messages={error?.fields.adults} />
          </fieldset>

          <label className="headless-booking__checkbox">
            <input type="checkbox" required checked={messageConsent} onChange={(event) => setMessageConsent(event.target.checked)} />
            <span>Send me appointment emails and one text reminder about this test ride. This does not add me to marketing.</span>
          </label>
          <FieldError messages={error?.fields.transactional_message_consent} />

          <div className="headless-booking__actions">
            <button type="button" className="headless-booking__secondary" onClick={() => setStep("slots")}>Change time</button>
            <button type="submit" className="headless-booking__primary" disabled={busy}>{busy ? "Saving…" : "Continue to waiver"}</button>
          </div>
        </form>
      )}

      {step === "waiver" && waiver && (
        <section aria-labelledby="waiver-heading">
          <a href="#ride-details-heading" className="headless-booking__back-link" onClick={(event) => { event.preventDefault(); setStep("details"); }}>
            <span aria-hidden="true">←</span> Back to ride details
          </a>
          <h3 id="waiver-heading" ref={stepHeadingRef} tabIndex={-1}>Test-ride waiver</h3>
          {waiver.preview_only && (
            <div className="headless-booking__preview-warning" data-testid="preview-waiver-warning" role="note">
              PREVIEW ONLY — NOT A LEGAL WAIVER
            </div>
          )}
          <div className="headless-booking__waiver-meta">Version {waiver.label}</div>
          <div className="headless-booking__waiver-text" tabIndex={0}>{waiver.legal_text}</div>

          <ol className="headless-booking__signer-list" aria-label="Adult waiver status" role="list">
            {riders.map((rider) => <li key={rider.position} data-signed={rider.signed || undefined}>{rider.adult_name}<span>{rider.signed ? "Signed" : "Needs signature"}</span></li>)}
          </ol>

          {nextRider && (
            <form onSubmit={signCurrentRider} className="headless-booking__signature-form">
              <h4>{nextRider.adult_name} signs now</h4>
              {nextRider.minors.length > 0 && <p>Covered minors: {nextRider.minors.join(", ")}</p>}
              <label>Type your full legal name
                <input required autoComplete="name" value={typedNames[nextRider.position] || ""} onChange={(event) => setTypedNames((current) => ({ ...current, [nextRider.position]: event.target.value }))} />
                <FieldError messages={error?.fields.typed_name} />
              </label>
              <label className="headless-booking__checkbox">
                <input type="checkbox" required checked={assents[nextRider.position] || false} onChange={(event) => setAssents((current) => ({ ...current, [nextRider.position]: event.target.checked }))} />
                <span>I have read and agree to the waiver shown above.</span>
              </label>
              <button type="submit" className="headless-booking__primary" disabled={busy}>{busy ? "Saving signature…" : `Sign as ${nextRider.adult_name}`}</button>
            </form>
          )}

          {!allSigned && riders.some((rider) => rider.signed) && (
            <button type="button" className="headless-booking__secondary" data-limit-reached={adults.length >= riderLimits.max_adults || undefined} disabled={adults.length >= riderLimits.max_adults} title={`Up to ${riderLimits.max_adults} adults per booking`} onClick={addAdultAfterSignature}>Add another adult rider</button>
          )}

          {allSigned && (
            <div className="headless-booking__finalize">
              <strong>Every adult has signed.</strong>
              <p>Your appointment is not confirmed until the next step succeeds.</p>
              <button type="button" className="headless-booking__primary" disabled={busy} onClick={() => void confirmBooking()}>{busy ? "Confirming…" : "Confirm test ride"}</button>
            </div>
          )}
        </section>
      )}

      {step === "confirmed" && (
        <section className="headless-booking__confirmed" role="status">
          <span aria-hidden="true">✓</span>
          <h3 ref={stepHeadingRef} tabIndex={-1}>Your test ride is confirmed.</h3>
          {confirmedSlot && <p><strong>{dateFormatter.format(new Date(confirmedSlot.start))} · {timeFormatter.format(new Date(confirmedSlot.start))}–{timeFormatter.format(new Date(confirmedSlot.end))} Mountain Time</strong><br />{shop.addressLine1}, {shop.addressLine2}</p>}
          <p>We will send your appointment confirmation and reminder. Call or text the shop if plans change.</p>
        </section>
      )}
    </div>
  );
}
